// Centralised per-page SEO + structured-data definitions.
// Imported by each route page via PageSEO component.

const SITE = "https://stationerywalaa.com";
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_luxury-afford/artifacts/h2jytaqw_WhatsApp%20Image%202026-04-25%20at%2018.06.24%20%284%29.jpeg";

const breadcrumbs = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE}${item.path}`,
  })),
});

const faqPage = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const service = ({ name, description, image, slug, serviceType }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  image: image || HERO_IMG,
  serviceType: serviceType || name,
  provider: {
    "@type": "Organization",
    name: "StationeryWalaa",
    url: SITE,
  },
  areaServed: [
    { "@type": "Country", name: "India" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "Canada" },
    { "@type": "Country", name: "Australia" },
    { "@type": "Country", name: "United Arab Emirates" },
  ],
  url: `${SITE}/services#${slug}`,
});

// Rendered as the FAQ accordion on /services AND mirrored into FAQPage JSON-LD.
export const SERVICES_FAQS = [
  {
    q: "Do you design luxury wedding invitations for Indian and destination weddings?",
    a: "Yes. Every luxury wedding invitation at StationeryWalaa is designed from scratch around your celebration — Hindu, Sikh, Muslim, Christian, Marwari, Bengali, Punjabi, South Indian and destination weddings. Hand-illustrated artwork, foil stamping, embossing and premium papers, finished in our Noida atelier and shipped worldwide.",
  },
  {
    q: "Can you make acrylic wedding invitations and wedding invitation boxes?",
    a: "Yes — acrylic wedding invitations, boxed invitation suites and welcome hamper boxes are designed on request. Because every acrylic invite and invitation box is fully bespoke, we finalise material, thickness, print style and inserts during a free WhatsApp consultation before sharing a custom quote.",
  },
  {
    q: "Do you create Save The Date cards and digital wedding invitations?",
    a: "We design printed Save The Date cards to match your main wedding invitation suite. For digital wedding invitations — animated e-invites, video invitations and WhatsApp save-the-dates — our sister studio InvitationWala (invitationwala.online) creates them with the same design team, so your print and digital stationery match perfectly.",
  },
  {
    q: "How does the custom design process work?",
    a: "Four steps: share your brief on WhatsApp or in a private consultation, receive a hand-crafted design proof, refine it through up to three revision rounds, then approve for print. Every order clears a 10-point quality audit before dispatch — nothing prints without your written sign-off.",
  },
  {
    q: "How is pricing decided for custom wedding invitations?",
    a: "Pricing is quoted per project after a free consultation. It depends on design complexity, paper stock, finishes such as foil or emboss, quantity and shipping destination. StationeryWalaa is a bespoke design studio — you always receive a personalised quote on WhatsApp, usually within a few hours.",
  },
  {
    q: "What are the delivery timelines for wedding stationery?",
    a: "Custom wedding invitation suites take 10–14 days after final design approval; reorders and simpler pieces ship in 5–7 business days. Urgent orders in Delhi NCR can be designed, printed and delivered within 24 hours through our Express service.",
  },
  {
    q: "Do you ship wedding invitations worldwide?",
    a: "Yes — we ship to 200+ countries via DHL Express, FedEx, Blue Dart and DTDC with full tracking. Typical delivery: USA and UK 5–7 days, Canada and Australia 6–8 days, UAE and Singapore 3–5 days.",
  },
  {
    q: "Can NRI couples order wedding invitations from abroad?",
    a: "Absolutely — most of our NRI clients in the USA, UK, Canada, Australia and UAE work with us entirely over WhatsApp and email. You share references, approve digital proofs remotely, and we print and deliver door-to-door. No India visit required.",
  },
];

// Home-page FAQ rich results (matches the on-page FAQ section on Home).
const HOME_FAQS = [
  {
    q: "Do you ship wedding invitation cards worldwide?",
    a: "Yes! StationeryWalaa ships custom wedding invitation cards worldwide via DTDC, Blue Dart, and DHL. We regularly deliver to the USA, UK, Canada, Australia, UAE, Singapore, Germany, and 200+ countries for NRI weddings and destination weddings.",
  },
  {
    q: "How long does international shipping take?",
    a: "International delivery to the USA, UK, Canada, and Australia typically takes 5–8 business days via DHL Express. UAE and Singapore: 3–5 days. We provide tracking for every order.",
  },
  {
    q: "Can I customise wedding invitations from outside India?",
    a: "Absolutely. Most of our NRI clients work with us over WhatsApp and email. We send proofs digitally, you approve, we print and ship. No need to be in India — we've delivered to couples in over 40 countries.",
  },
  {
    q: "What is the minimum order quantity for wedding cards?",
    a: "We accept orders starting from 50 pieces for printed wedding invitation cards. For luggage tags, caricature cards and tent cards, the minimum is 25 pieces. Smaller sample orders are available for trial.",
  },
  {
    q: "Do you offer Delhi NCR 24-hour express delivery?",
    a: "Yes — same-day pickup and next-day delivery anywhere in Delhi, Gurgaon, Noida, Ghaziabad, and Faridabad. Perfect for last-minute mehendi cards, urgent reception invites, or same-week welcome boxes.",
  },
  {
    q: "Can you design Indian, destination and themed wedding invitations?",
    a: "Yes — Hindu, Sikh, Muslim, Christian, Marwari, Bengali, South Indian, Punjabi, destination weddings, and themed weddings. Each invitation suite is hand-illustrated in our atelier and printed to match your story, palette, and culture.",
  },
];

export const SEO = {
  home: {
    title: "Luxury Wedding Invitations · Custom Wedding Cards Worldwide | StationeryWalaa",
    description:
      "StationeryWalaa crafts luxury wedding invitations, premium wedding stationery, save the date cards and invitation boxes — hand-finished in India, shipped worldwide to USA, UK, Canada, Australia and UAE. Free WhatsApp consultation for Indian, destination and NRI weddings.",
    path: "/",
    jsonLd: [
      breadcrumbs([{ name: "Home", path: "/" }]),
      faqPage(HOME_FAQS),
    ],
  },

  about: {
    title: "About StationeryWalaa · Luxury Wedding Stationery Atelier in Noida, India",
    description:
      "Meet the atelier behind StationeryWalaa — a small, obsessive team designing premium wedding invitations and custom wedding stationery for couples in 40+ countries. Every piece clears a 10-point quality audit before it ships.",
    path: "/about",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About StationeryWalaa",
        description:
          "Luxury wedding stationery atelier in Noida crafting premium wedding invitation suites for Indian, destination and NRI weddings.",
        url: `${SITE}/about`,
      },
    ],
  },

  team: {
    title: "Meet the Design Team · StationeryWalaa Wedding Stationery Atelier",
    description:
      "Meet the designers, illustrators, foil & letterpress specialists and finishing artisans behind StationeryWalaa. Eight specialist crafts, one Noida atelier — 5,000+ luxury wedding invitation suites dispatched to couples in 40+ countries.",
    path: "/team",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Meet the Team", path: "/team" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "Meet the StationeryWalaa Design Team",
        description:
          "The specialist roles inside the StationeryWalaa atelier — creative direction, invitation design, illustration and calligraphy, hot foil and letterpress, paper curation, die-cutting, quality control and worldwide client concierge.",
        url: `${SITE}/team`,
        about: {
          "@type": "Organization",
          name: "StationeryWalaa",
          url: SITE,
        },
      },
    ],
  },

  services: {
    title: "Premium Wedding Invitation Cards, Boxes & Custom Stationery | StationeryWalaa",
    description:
      "Customized wedding invitations, acrylic invites, invitation boxes, luggage tags, caricature tags, hamper tags, tent cards and save the date cards. Bespoke design for Indian, destination and NRI weddings — worldwide shipping after a free consultation.",
    path: "/services",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Collection", path: "/services" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Premium Wedding Stationery Collection",
        description:
          "Custom luggage tags, caricature tags, hamper tags, tent cards and complete luxury wedding invitation suites — designed and printed after private consultation.",
        url: `${SITE}/services`,
      },
      service({
        name: "Custom Luggage Tags for Weddings",
        description:
          "Hand-illustrated, foil-stamped luggage tags for destination weddings and hospitality welcome boxes. Custom shapes, monogrammed, sets of 4 to 200. Priced after consultation.",
        slug: "luggage-tags",
        serviceType: "Custom Wedding Luggage Tag Design & Print",
      }),
      service({
        name: "Caricature Luggage Tags",
        description:
          "Hand-illustrated couple-portrait caricature tags for destination weddings. A one-of-a-kind heirloom keepsake for every wedding guest.",
        slug: "caricature",
        serviceType: "Caricature Wedding Card Design",
      }),
      service({
        name: "Hamper Tags & Door Danglers",
        description:
          "Monogrammed welcome-room danglers and hamper tags for multi-day wedding celebrations. Foil, emboss and die-cut finishes available.",
        slug: "hamper-tags",
        serviceType: "Wedding Hamper Tag Design & Print",
      }),
      service({
        name: "Wedding Tent Cards & Place Cards",
        description:
          "Curated tent cards, table place cards, and ceremony menus designed for sit-down dinners and reception dining setups.",
        slug: "tent-cards",
        serviceType: "Wedding Tent Card Design & Print",
      }),
      faqPage(SERVICES_FAQS),
    ],
  },

  portfolio: {
    title: "Luxury Wedding Invitation Portfolio · Real Wedding Stationery | StationeryWalaa",
    description:
      "Browse 200+ real luxury wedding invitation suites, save the date cards, luggage tags, hamper tags and tent cards designed for couples in India, USA, UK, UAE and beyond. Every piece hand-finished in our atelier — filter by category.",
    path: "/portfolio",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Portfolio", path: "/portfolio" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Luxury Wedding Stationery Portfolio",
        description:
          "Real wedding invitation lookbook from StationeryWalaa — Indian weddings, destination weddings and NRI celebrations.",
        url: `${SITE}/portfolio`,
      },
    ],
  },

  contact: {
    title: "Contact StationeryWalaa · Custom Wedding Invitation Quote on WhatsApp",
    description:
      "Get a custom quote for luxury wedding invitations on WhatsApp +91 91253 52918, email stationerywala1@gmail.com, or visit our studio at Spectrum Mall, Sector 75, Noida. Free design consultation with worldwide shipping.",
    path: "/contact",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact StationeryWalaa",
        url: `${SITE}/contact`,
      },
    ],
  },

  book: {
    title: "Book a Free Wedding Invitation Design Consultation | StationeryWalaa",
    description:
      "Schedule a private 30-minute wedding stationery consultation with a senior designer — over Google Meet or at our Noida studio. Discuss custom wedding invitations, samples, timelines and worldwide delivery at your pace.",
    path: "/book",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Book Meeting", path: "/book" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Wedding Stationery Design Consultation",
        description:
          "Private 30-minute consultation with a senior wedding stationery designer — over Google Meet or at our Noida studio.",
        provider: { "@type": "Organization", name: "StationeryWalaa", url: SITE },
        areaServed: "Worldwide",
        url: `${SITE}/book`,
        serviceType: "Wedding Stationery Design Consultation",
      },
    ],
  },

  expressDelhi: {
    title: "24-Hour Wedding Card Printing & Delivery in Delhi NCR | StationeryWalaa",
    description:
      "Urgent wedding invitation cards in Delhi, Gurgaon, Noida, Ghaziabad and Faridabad — designed, printed and delivered within 24 hours. Same-day pickup for last-minute mehendi, sangeet and reception invites.",
    path: "/express-delhi-ncr",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Express Delhi NCR", path: "/express-delhi-ncr" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Delhi NCR 24-Hour Express Wedding Cards",
        description:
          "Same-day pickup, next-day delivery of custom wedding invitations across Delhi, Gurgaon, Noida, Ghaziabad and Faridabad. Designed, printed and dispatched within 24 hours.",
        provider: { "@type": "Organization", name: "StationeryWalaa", url: SITE },
        areaServed: [
          { "@type": "City", name: "Delhi" },
          { "@type": "City", name: "Gurgaon" },
          { "@type": "City", name: "Noida" },
          { "@type": "City", name: "Ghaziabad" },
          { "@type": "City", name: "Faridabad" },
        ],
        serviceType: "Same-day wedding stationery design, printing and delivery",
        url: `${SITE}/express-delhi-ncr`,
      },
    ],
  },

  career: {
    title: "Careers at StationeryWalaa · Wedding Stationery Design Jobs in Noida",
    description:
      "Join the StationeryWalaa atelier — open roles for graphic designers, print production, client relations and social-media creators. On-site in Sector 75 Noida. Apply on WhatsApp.",
    path: "/career",
    jsonLd: [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Careers", path: "/career" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title: "Graphic Designer · Wedding Stationery",
        description:
          "Hand-illustrate wedding invitation suites in Illustrator and InDesign, work directly with bride and groom families.",
        datePosted: "2026-02-01",
        employmentType: "FULL_TIME",
        hiringOrganization: {
          "@type": "Organization",
          name: "StationeryWalaa",
          sameAs: SITE,
        },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            streetAddress: "A1514c, Spectrum Mall, Sector 75",
            addressLocality: "Noida",
            addressRegion: "Uttar Pradesh",
            postalCode: "201301",
            addressCountry: "IN",
          },
        },
      },
    ],
  },
};
